import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { reportsExtendedApi } from "../../services/reports-extended.api";
import { useReportData } from "../../hooks/useReportData";
import { apiClient } from "@/lib/apiClient";
import { Projects, Target } from "@/components/icons";
import styles from "./EnrollmentFunnelReportPage.module.scss";

// ---------- Types (local — kept in-file to avoid touching shared services) ----------
type FunnelStage = "enrolled" | "attended" | "completed" | "certified";

interface FunnelItem {
  educationID: string;
  title: string;
  enrolled: number;
  attended: number;
  completed: number;
  certified: number;
  attendedRate: number;
  completedRate: number;
  certifiedRate: number;
  overallConversionPercent: number;
}

interface FunnelStagePoint {
  stage: FunnelStage;
  count: number;
  ratePercent: number;
}

interface MonthlyFunnelPoint {
  month: string;
  enrolled: number;
  attended: number;
  completed: number;
  certified: number;
}

interface FunnelDropoffPoint {
  label: string;
  dropoffPercent: number;
}

interface EnrollmentFunnelSummary {
  totalEnrolled: number;
  totalAttended: number;
  totalCompleted: number;
  totalCertified: number;
  attendedRate: number;
  completedRate: number;
  certifiedRate: number;
  overallConversionPercent: number;
}

interface EnrollmentFunnelResponse {
  summary: EnrollmentFunnelSummary;
  funnelStages: FunnelStagePoint[];
  monthlyFunnel: MonthlyFunnelPoint[];
  worstDropoffEducations: FunnelDropoffPoint[];
  items: FunnelItem[];
  count: number;
}

interface EnrollmentFunnelParams {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  educationID?: string;
  departmentID?: string;
}

// ---------- Local fetcher ----------
const fetchEnrollmentFunnel = async (
  params: EnrollmentFunnelParams,
  signal: AbortSignal,
): Promise<EnrollmentFunnelResponse> => {
  const { data } = await apiClient.get(
    "/api/admin/reports/enrollment-funnel",
    { params, signal },
  );
  const body = data as { data?: EnrollmentFunnelResponse } | EnrollmentFunnelResponse;
  if (body && typeof body === "object" && "data" in body && body.data) {
    return body.data;
  }
  return body as EnrollmentFunnelResponse;
};

// ---------- Helpers ----------
const PAGE_SIZE = 20;

const formatPercent = (n: number | null | undefined) =>
  n == null ? "—" : `${n.toFixed(1)}%`;

const formatNumber = (n: number | null | undefined) =>
  n == null ? "—" : new Intl.NumberFormat("tr-TR").format(n);

const STAGE_LABELS: Record<FunnelStage, string> = {
  enrolled: "Kayıt",
  attended: "Katıldı",
  completed: "Tamamladı",
  certified: "Sertifika",
};

const STAGE_COLORS: Record<FunnelStage, string> = {
  enrolled: "#4f46e5",
  attended: "#0ea5e9",
  completed: "#10b981",
  certified: "#f59e0b",
};

const EnrollmentFunnelReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: EnrollmentFunnelParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      departmentID: filter.departmentID,
      search: filter.search?.trim() || undefined,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    EnrollmentFunnelResponse,
    EnrollmentFunnelParams
  >({
    fetcher: (p, signal) => fetchEnrollmentFunnel(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;
  const funnelStages = data?.funnelStages ?? [];
  const monthlyFunnel = data?.monthlyFunnel ?? [];
  const worstDropoffEducations = data?.worstDropoffEducations ?? [];

  // ---------- Columns ----------
  const columns: TableColumn<FunnelItem>[] = [
    {
      header: t("reports.enrollmentFunnel.headerEducation", "EĞİTİM"),
      accessor: (row) => (
        <span className={styles.titleCell} title={row.title}>
          {row.title}
        </span>
      ),
    },
    {
      header: t("reports.enrollmentFunnel.headerEnrolled", "KAYIT"),
      accessor: (row) => formatNumber(row.enrolled),
    },
    {
      header: t("reports.enrollmentFunnel.headerAttended", "KATILDI"),
      accessor: (row) => (
        <span>
          {formatNumber(row.attended)}
          <span className={styles.muted}> ({formatPercent(row.attendedRate)})</span>
        </span>
      ),
    },
    {
      header: t("reports.enrollmentFunnel.headerCompleted", "TAMAMLAYAN"),
      accessor: (row) => (
        <span>
          {formatNumber(row.completed)}
          <span className={styles.muted}> ({formatPercent(row.completedRate)})</span>
        </span>
      ),
    },
    {
      header: t("reports.enrollmentFunnel.headerCertified", "SERTİFİKA"),
      accessor: (row) => (
        <span>
          {formatNumber(row.certified)}
          <span className={styles.muted}> ({formatPercent(row.certifiedRate)})</span>
        </span>
      ),
    },
    {
      header: t(
        "reports.enrollmentFunnel.headerOverallConversion",
        "ORT DÖNÜŞÜM %",
      ),
      accessor: (row) => {
        const v = row.overallConversionPercent;
        const cls =
          v >= 60 ? styles.kpiGood : v >= 30 ? styles.kpiMid : styles.kpiBad;
        return <span className={cls}>{formatPercent(v)}</span>;
      },
    },
  ];

  return (
    <ReportDetailLayout
      title={t("reports.enrollmentFunnel.title", "Eğitim Funnel")}
      categoryLabel={t(
        "reportCatalog.categories.strategic.title",
        "Stratejik",
      )}
      description={t(
        "reports.enrollmentFunnel.description",
        "Kayıt → katılım → tamamlama → sertifika dönüşüm hunisi ve dropoff analizi.",
      )}
      exportSlot={
        <ReportExportButtons
          reportType="ENROLLMENT_FUNNEL"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="egitim-funnel"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
        />
      }
    >
      {/* ---------- KPI ---------- */}
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t("reports.enrollmentFunnel.kpiEnrolled", "Toplam Kayıt")}
          value={summary ? formatNumber(summary.totalEnrolled) : "—"}
          icon={<Projects width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.enrollmentFunnel.kpiAttended", "Katılım %")}
          value={summary ? formatPercent(summary.attendedRate) : "—"}
          icon={<Target width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.enrollmentFunnel.kpiCompleted", "Tamamlama %")}
          value={summary ? formatPercent(summary.completedRate) : "—"}
          icon={<Target width={18} height={18} />}
          accent="success"
        />
        <ReportKPICard
          title={t("reports.enrollmentFunnel.kpiCertified", "Sertifika %")}
          value={summary ? formatPercent(summary.certifiedRate) : "—"}
          icon={<Target width={18} height={18} />}
          accent="warning"
          footnote={
            summary
              ? t("reports.enrollmentFunnel.kpiOverall", "Toplam dönüşüm: ") +
                formatPercent(summary.overallConversionPercent)
              : undefined
          }
        />
      </div>

      {/* ---------- Charts ---------- */}
      <div className={styles.chartGrid}>
        <ReportChart
          title={t("reports.enrollmentFunnel.chartStages", "Funnel Aşamaları")}
          type="bar"
          isLoading={isLoading}
          isEmpty={funnelStages.length === 0}
          series={[
            {
              name: t("reports.enrollmentFunnel.chartStagesSeries", "Adet"),
              data: funnelStages.map((s) => s.count),
            },
          ]}
          options={{
            chart: { toolbar: { show: false } },
            xaxis: {
              categories: funnelStages.map(
                (s) => `${STAGE_LABELS[s.stage]} (${s.ratePercent.toFixed(1)}%)`,
              ),
            },
            colors: funnelStages.map((s) => STAGE_COLORS[s.stage]),
            plotOptions: {
              bar: {
                horizontal: true,
                distributed: true,
                barHeight: "65%",
                borderRadius: 6,
              },
            },
            dataLabels: { enabled: true },
            legend: { show: false },
          }}
        />

        <ReportChart
          title={t(
            "reports.enrollmentFunnel.chartMonthly",
            "Aylık Funnel Trendi",
          )}
          type="area"
          isLoading={isLoading}
          isEmpty={monthlyFunnel.length === 0}
          series={[
            {
              name: STAGE_LABELS.enrolled,
              data: monthlyFunnel.map((p) => p.enrolled),
            },
            {
              name: STAGE_LABELS.attended,
              data: monthlyFunnel.map((p) => p.attended),
            },
            {
              name: STAGE_LABELS.completed,
              data: monthlyFunnel.map((p) => p.completed),
            },
            {
              name: STAGE_LABELS.certified,
              data: monthlyFunnel.map((p) => p.certified),
            },
          ]}
          options={{
            xaxis: { categories: monthlyFunnel.map((p) => p.month) },
            colors: [
              STAGE_COLORS.enrolled,
              STAGE_COLORS.attended,
              STAGE_COLORS.completed,
              STAGE_COLORS.certified,
            ],
            stroke: { curve: "smooth", width: 2 },
            fill: { type: "gradient", gradient: { opacityFrom: 0.45, opacityTo: 0.05 } },
            chart: { stacked: true },
          }}
        />

        <ReportChart
          title={t(
            "reports.enrollmentFunnel.chartDropoff",
            "En Yüksek Dropoff (Top 10)",
          )}
          type="bar"
          isLoading={isLoading}
          isEmpty={worstDropoffEducations.length === 0}
          series={[
            {
              name: t("reports.enrollmentFunnel.dropoffSeries", "Dropoff %"),
              data: worstDropoffEducations.map((d) => d.dropoffPercent),
            },
          ]}
          options={{
            xaxis: { categories: worstDropoffEducations.map((d) => d.label) },
            colors: ["#ef4444"],
            plotOptions: {
              bar: { columnWidth: "50%", borderRadius: 4 },
            },
          }}
        />
      </div>

      {/* ---------- Table ---------- */}
      <Table
        columns={columns}
        data={items}
        isLoading={isLoading}
        contentBasedHeight
        getRowId={(row) => row.educationID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t("reports.enrollmentFunnel.noData", "Veri yüklenemedi.")
            : t(
                "reports.enrollmentFunnel.noEducations",
                "Eğitim kaydı bulunamadı.",
              )
        }
      />
    </ReportDetailLayout>
  );
};

export default EnrollmentFunnelReportPage;
